import * as React from 'react';
import { Slider } from '@material-ui/core';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { SlidersSection, SliderContainer, SliderLabel, SliderValue } from './styled';
import { SubscriptionAddonDefinition, SubscriptionAddonTier } from '../../models';
import { formatCurrency } from './utils';

export interface UsageSlidersProps {
    includedUsers: number;
    addons: Record<string, SubscriptionAddonDefinition>;
    onUsersChange: (tier: SubscriptionAddonTier | null) => void;
    onSmsChange: (tier: SubscriptionAddonTier | null) => void;
    onWhatsAppChange: (tier: SubscriptionAddonTier | null) => void;
    selectedUsersTier: SubscriptionAddonTier | null;
    selectedSmsTier: SubscriptionAddonTier | null;
    selectedWhatsAppTier: SubscriptionAddonTier | null;
    currentSmsAllowed?: number;
    currentWhatsAppAllowed?: number;
    currentUsersCount?: number;
}

const UsageSliders: React.FC<UsageSlidersProps> = ({
    includedUsers,
    addons,
    onUsersChange,
    onSmsChange,
    onWhatsAppChange,
    selectedUsersTier,
    selectedSmsTier,
    selectedWhatsAppTier,
    currentSmsAllowed,
    currentWhatsAppAllowed,
    currentUsersCount,
}) => {
    const t = useTranslations();

    const usersAddon = addons['Users'] || addons['users'];
    const smsAddon = addons['Sms'] || addons['sms'];
    const whatsAppAddon = addons['WhatsApp'] || addons['whatsapp'] || addons['whatsApp'];

    const usersTiers = usersAddon?.tiers || [];
    const smsTiers = smsAddon?.tiers || [];
    const whatsAppTiers = whatsAppAddon?.tiers || [];


    const handleUsersSliderChange = (_: any, value: number | number[]) => {
        const index = typeof value === 'number' ? value : value[0];
        onUsersChange(index === 0 ? null : usersTiers[index - 1]);
    };

    const handleSmsSliderChange = (_: any, value: number | number[]) => {
        const index = typeof value === 'number' ? value : value[0];
        onSmsChange(index === 0 ? null : smsTiers[index - 1]);
    };

    const handleWhatsAppSliderChange = (_: any, value: number | number[]) => {
        const index = typeof value === 'number' ? value : value[0];
        onWhatsAppChange(index === 0 ? null : whatsAppTiers[index - 1]);
    };


    const usersMarks = [
        { value: 0, label: `${includedUsers}` },
        ...usersTiers.map((tier, idx) => ({
            value: idx + 1,
            label: includedUsers + tier.quantity,
        })),
    ];

    const smsMarks = [
        { value: 0, label: '100' },
        ...smsTiers.map((tier, idx) => ({
            value: idx + 1,
            label: `${tier.quantity} SMS`,
        })),
    ];

    const whatsAppMarks = [
        { value: 0, label: '0' },
        ...whatsAppTiers.map((tier, idx) => ({
            value: idx + 1,
            label: `${tier.quantity} WhatsApp`,
        })),
    ];

    const selectedUsersIndex = selectedUsersTier
        ? usersTiers.findIndex((t) => t.quantity === selectedUsersTier.quantity && t.pricePerMonth === selectedUsersTier.pricePerMonth) + 1
        : 0;

    const selectedSmsIndex = selectedSmsTier
        ? smsTiers.findIndex((t) => t.quantity === selectedSmsTier.quantity && t.pricePerMonth === selectedSmsTier.pricePerMonth) + 1
        : 0;

    const selectedWhatsAppIndex = selectedWhatsAppTier
        ? whatsAppTiers.findIndex((t) => t.quantity === selectedWhatsAppTier.quantity && t.pricePerMonth === selectedWhatsAppTier.pricePerMonth) + 1
        : 0;

    const isCurrentUsers = currentUsersCount !== undefined && selectedUsersTier?.quantity === currentUsersCount;
    const isCurrentSms = currentSmsAllowed !== undefined && selectedSmsTier?.quantity === currentSmsAllowed;
    const isCurrentWhatsApp = currentWhatsAppAllowed !== undefined && selectedWhatsAppTier?.quantity === currentWhatsAppAllowed;

    return (
        <SlidersSection>
            <SliderContainer>
                <SliderLabel>{t(TK.numberOfActiveUsers)}</SliderLabel>
                <div className="slider-row">
                    <Slider
                        value={selectedUsersIndex}
                        onChange={handleUsersSliderChange}
                        min={0}
                        max={usersTiers.length}
                        step={1}
                        valueLabelDisplay="auto"
                        marks={usersMarks}
                    />
                    <SliderValue>
                        {selectedUsersTier ? (
                            isCurrentUsers ? (
                                <>
                                    {`+${formatCurrency(selectedUsersTier.pricePerMonth)}`}
                                    <br />
                                    <span className="included-text">{t(TK.current)}</span>
                                </>
                            ) : (
                                `+${formatCurrency(selectedUsersTier.pricePerMonth)}`
                            )
                        ) : (
                            <span className="included-text">{t(TK.included)}</span>
                        )}
                    </SliderValue>
                </div>
            </SliderContainer>

            <SliderContainer>
                <SliderLabel>{t(TK.monthlySmsBundle)}</SliderLabel>
                <div className="slider-row">
                    <Slider
                        value={selectedSmsIndex}
                        onChange={handleSmsSliderChange}
                        min={0}
                        max={smsTiers.length}
                        step={1}
                        valueLabelDisplay="auto"
                        marks={smsMarks}
                    />
                    <SliderValue>
                        {selectedSmsTier ? (
                            isCurrentSms ? (
                                <>
                                    {`+${formatCurrency(selectedSmsTier.pricePerMonth)}`}
                                    <br />
                                    <span className="included-text">{t(TK.current)}</span>
                                </>
                            ) : (
                                `+${formatCurrency(selectedSmsTier.pricePerMonth)}`
                            )
                        ) : (
                            <span className="included-text">{t(TK.included)}</span>
                        )}
                    </SliderValue>
                </div>
            </SliderContainer>

            <SliderContainer>
                <SliderLabel>{t(TK.monthlyWhatsAppBundle)}</SliderLabel>
                <div className="slider-row">
                    <Slider
                        value={selectedWhatsAppIndex}
                        onChange={handleWhatsAppSliderChange}
                        min={0}
                        max={whatsAppTiers.length}
                        step={1}
                        valueLabelDisplay="auto"
                        marks={whatsAppMarks}
                    />
                    <SliderValue>
                        {selectedWhatsAppTier ? (
                            isCurrentWhatsApp ? (
                                <>
                                    {`+${formatCurrency(selectedWhatsAppTier.pricePerMonth)}`}
                                    <br />
                                    <span className="included-text">{t(TK.current)}</span>
                                </>
                            ) : (
                                `+${formatCurrency(selectedWhatsAppTier.pricePerMonth)}`
                            )
                        ) : (
                            <span className="included-text">{t(TK.included)}</span>
                        )}
                    </SliderValue>
                </div>
            </SliderContainer>
        </SlidersSection>
    );
};

export default UsageSliders;
